/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState, useEffect } from 'react';
import { withMyTheme } from '../theme/theme';
import { mobileCss } from '../theme/isMobile';
import { MyInput } from '../components/input/MyInput';
import { MyButton } from '../components/button/MyButton';
import { CircularProgress } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';

const ContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2rem;
    background-color: ${theme.palette.background.default};
`);

const FormStyle = withMyTheme((theme) => css`
    background-color: ${theme.palette.primary.main};
    padding: 3rem;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    width: 100%;
    max-width: 500px;
    
    ${mobileCss(`
        padding: 2rem;
        max-width: 90%;
    `)}
`);

const TitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.secondary.contrastText};
    margin-bottom: 2rem;
    text-align: center;
    font-family: ${theme.typography.h1.fontFamily};
`);

const FormFieldsStyle = css`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2rem;
`;

const InputFieldStyle = withMyTheme((theme) => css`
    & .MuiInputLabel-root {
        color: ${theme.palette.secondary.contrastText};
        font-family: ${theme.typography.body1.fontFamily};
    }
    & .MuiOutlinedInput-root {
        font-family: ${theme.typography.body1.fontFamily};
        color: ${theme.palette.secondary.contrastText};
        & fieldset {
            border-color: ${theme.palette.secondary.contrastText};
        }
        &:hover fieldset {
            border-color: ${theme.palette.secondary.contrastText};
        }
        &.Mui-focused fieldset {
            border-color: ${theme.palette.secondary.contrastText};
        }
    }
    & .MuiInputBase-input::placeholder {
        color: ${theme.palette.secondary.contrastText};
        opacity: 0.7;
    }
`);

const ButtonStyle = withMyTheme((theme) => css`
    background-color: ${theme.palette.primary.dark};
    color: ${theme.palette.primary.contrastText};
    position: relative;
    &:hover {
        background-color: ${theme.palette.primary.main};
    }
`);

const ErrorMessageStyle = withMyTheme((theme) => css`
    color: ${theme.palette.error.main};
    margin-top: 1rem;
    text-align: center;
    font-family: ${theme.typography.body1.fontFamily};
`);

const LoadingSpinnerStyle = withMyTheme((theme) => css`
    position: absolute;
    color: ${theme.palette.primary.contrastText};
`);

const ButtonTextStyle = withMyTheme((theme, isLoading = false) => css`
    visibility: ${isLoading ? 'hidden' : 'visible'};
`);

// Function to decode base64 string
const decodeBase64 = (str: string): string => {
    try {
        return atob(str);
    } catch (e) {
        console.error('Error decoding base64 string:', e);
        return '';
    }
};

export const SuperAdmin = () => {
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [token, setToken] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [redirect, setRedirect] = useState(false);
    const [autoValidate, setAutoValidate] = useState(false);
    
    // Define validateRepositories function first so we can reference it in useEffect
    const validateRepositories = async () => {
        if (!username || !token) {
            setErrorMessage('Proszę wprowadzić nazwę użytkownika i token');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        try {
            // Fetch repositories from GitHub API
            const response = await fetch(`https://api.github.com/users/${username}/repos`, {
                headers: {
                    'Authorization': `token ${token}`,
                    'Accept': 'application/vnd.github.v3+json'
                }
            });

            if (!response.ok) {
                throw new Error('Nie udało się uwierzytelnić z GitHub. Sprawdź swoje dane logowania.');
            }

            const repositories = await response.json();
            
            // Check if required repositories exist
            const requiredRepos = ['agata-sawicka-website'];
            const foundRepos = requiredRepos.filter(repo => 
                repositories.some((r: any) => r.name === repo)
            );

            if (foundRepos.length === requiredRepos.length) {
                // Store credentials in local storage for later use
                localStorage.setItem('github_username', username);
                localStorage.setItem('github_token', token);
                
                // Redirect to config viewer
                setRedirect(true);
            } else {
                const missingRepos = requiredRepos.filter(repo => !foundRepos.includes(repo));
                setErrorMessage(`Brak wymaganych repozytoriów: ${missingRepos.join(', ')}`);
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Wystąpił nieznany błąd');
        } finally {
            setIsLoading(false);
        }
    };
    
    useEffect(() => {
        // Check for stored credentials first
        const storedUsername = localStorage.getItem('github_username');
        const storedToken = localStorage.getItem('github_token');
        
        if (storedUsername && storedToken) {
            setRedirect(true);
            return;
        }
        
        // Check for query parameters
        const queryParams = new URLSearchParams(location.search);
        const userParam = queryParams.get('u');
        const passParam = queryParams.get('p');
        
        if (userParam && passParam) {
            setUsername(userParam);
            
            try {
                const decodedPass = decodeBase64(passParam);
                setToken(decodedPass);
                
                setAutoValidate(true);
            } catch (error) {
                console.error('Error decoding password:', error);
                setErrorMessage('Nieprawidłowy format hasła');
            }
        }
    }, [location.search]);
    
    // Second useEffect to handle auto-validation after state updates
    useEffect(() => {
        if (autoValidate && username && token) {
            validateRepositories();
            setAutoValidate(false);
        }
    }, [autoValidate, username, token]);

    const handleUsernameChange = (value: string) => {
        setUsername(value);
        setErrorMessage('');
    };

    const handleTokenChange = (value: string) => {
        setToken(value);
        setErrorMessage('');
    };

    if (redirect) {
        return <Navigate to="/super-admin/offers" />;
    }

    return (
        <div css={ContainerStyle}>
            <div css={FormStyle}>
                <h1 css={TitleStyle}>Panel Administratora</h1>
                
                <div css={FormFieldsStyle}>
                    <MyInput
                        value={username}
                        label="Nazwa użytkownika GitHub"
                        placeholder="Wprowadź nazwę użytkownika GitHub"
                        onChange={handleUsernameChange}
                        additionalCss={InputFieldStyle}
                        required
                    />
                    
                    <MyInput
                        value={token}
                        label="Token dostępu GitHub (PAT)"
                        placeholder="Wprowadź token PAT"
                        onChange={handleTokenChange}
                        additionalCss={InputFieldStyle}
                        type="password"
                        required
                    />
                </div>

                {errorMessage && <div css={ErrorMessageStyle}>{errorMessage}</div>}

                <div css={css`display: flex; justify-content: center; margin-top: 1rem;`}>
                    <MyButton
                        variant="contained"
                        colorVariant="primary"
                        additionalCss={ButtonStyle}
                        onClick={validateRepositories}
                        disabled={isLoading || !username || !token}
                    >
                        {isLoading && <CircularProgress size={24} css={LoadingSpinnerStyle} />}
                        <span css={(theme) => ButtonTextStyle(theme, isLoading)}>
                            Weryfikuj dostęp
                        </span>
                    </MyButton>
                </div>
            </div>
        </div>
    );
};
